/**
 * Group Vote Service v2
 *
 * Unified group vote system: one data model, two creation entry points.
 * - Quick vote (from venue page): 1 activity pre-filled, organizer proposes dates
 * - Open vote (from dashboard): N activities + N date proposals (Doodle-style)
 *
 * Voters are always anonymous (session ID in localStorage).
 * Organizer creation requires a FullBook account.
 *
 * Vote model (open votes):
 *   Voting on a slot IS the vote for both the activity and the time.
 *   No separate activity option votes are used for open votes.
 *   Winner = slot with most votes → its activity_option_id identifies the winning activity.
 *   Activity vote tallies shown in UI are unique voter counts derived from slot votes.
 */

import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://tfchhwvznjbfpigluznd.supabase.co';

// ─── Core types ──────────────────────────────────────────────────────────────

export interface GroupVoteOptionVote {
  id: string;
  voterName: string;
  voterSessionId: string;
  createdAt: string;
}

export interface GroupVoteActivityOption {
  id: string;
  groupVoteId: string;
  activityId: string | null;
  name: string;
  emoji: string | null;
  pricePerPerson: number | null;
  notes: string | null;
  sortOrder: number;
  votes: GroupVoteOptionVote[]; // kept for backward compat (quick votes), empty for open votes
}

export interface GroupVoteSlotVote {
  id: string;
  voterName: string;
  voterSessionId: string;
  createdAt: string;
}

export interface GroupVoteSlot {
  id: string;
  groupVoteId: string;
  activityOptionId: string | null;
  venueAvailabilityId: string | null;
  slotDate: string;
  slotTime: string;
  votesCount: number;
  sortOrder: number;
  notificationSentAt: string | null;
  votes: GroupVoteSlotVote[];
  isFullBook: boolean;
}

export type GroupVoteSlotWithVotes = GroupVoteSlot;

export interface GroupVoteComment {
  id: string;
  groupVoteId: string;
  voterName: string;
  voterSessionId: string;
  body: string;
  createdAt: string;
}

export interface GroupVote {
  id: string;
  title: string;
  activityName: string;
  activityId: string | null;
  venueName: string | null;
  maxParticipants: number;
  numberOfGames: number;
  organizerId: string;
  status: 'active' | 'completed' | 'expired';
  expiresAt: string;
  createdAt: string;
  activityOptions: GroupVoteActivityOption[];
  slots: GroupVoteSlot[];
  comments: GroupVoteComment[];
}

// ─── Creation params ──────────────────────────────────────────────────────────

export interface ActivityOptionInput {
  activityId?: string;
  name: string;
  emoji?: string;
  pricePerPerson?: number;
  notes?: string;
}

export interface DateSlotInput {
  date: string;
  time: string;
  venueAvailabilityId?: string;
  activityOptionId?: string;
}

export interface CreateGroupVoteParams {
  title: string;
  activityOptions: ActivityOptionInput[];
  dateSlots: DateSlotInput[];
  maxParticipants: number;
  numberOfGames?: number;
  organizerId: string;
}

/** Quick-create params (from booking form, single activity + auto-generated slots) */
export interface CreateGroupVoteQuickParams {
  activityId: string;
  activityName: string;
  title?: string;
  maxParticipants: number;
  numberOfGames?: number;
  organizerId: string;
  startDate?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatSlotTime(time: string): string {
  if (typeof time !== 'string') return '00:00';
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : time;
}

function deriveTitle(activityName: string): string {
  return `Vote pour ${activityName}`;
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

export type GetGroupVoteResult =
  | { success: true; vote: GroupVote }
  | { success: false; reason: 'not_found' | 'fetch_error' };

export async function getGroupVote(voteId: string): Promise<GetGroupVoteResult> {
  const { data: voteRow, error: voteError } = await supabase
    .from('group_votes')
    .select('*')
    .eq('id', voteId)
    .single();

  if (voteError || !voteRow) {
    const isNotFound = voteError?.code === 'PGRST116';
    return { success: false, reason: isNotFound ? 'not_found' : 'fetch_error' };
  }

  const [optionsResult, commentsResult] = await Promise.all([
    supabase
      .from('group_vote_activity_options')
      .select('id, group_vote_id, activity_id, name, emoji, price_per_person, notes, sort_order')
      .eq('group_vote_id', voteId)
      .order('sort_order', { ascending: true }),
    supabase
      .from('group_vote_comments')
      .select('id, group_vote_id, voter_name, voter_session_id, body, created_at')
      .eq('group_vote_id', voteId)
      .order('created_at', { ascending: false }),
  ]);

  const optionRows = optionsResult.data ?? [];
  const commentRows = commentsResult.data ?? [];

  // Legacy option votes (backward compat for quick votes)
  const optionVotesResult = optionRows.length > 0
    ? await supabase
        .from('group_vote_option_votes')
        .select('id, group_vote_option_id, voter_name, voter_session_id, created_at')
        .in('group_vote_option_id', optionRows.map((o) => o.id))
    : { data: [], error: null };

  const optionVotesByOption = new Map<string, GroupVoteOptionVote[]>();
  (optionVotesResult.data ?? []).forEach((v) => {
    const list = optionVotesByOption.get(v.group_vote_option_id) ?? [];
    list.push({ id: v.id, voterName: v.voter_name, voterSessionId: v.voter_session_id, createdAt: v.created_at });
    optionVotesByOption.set(v.group_vote_option_id, list);
  });

  const activityOptions: GroupVoteActivityOption[] = optionRows.map((o) => ({
    id: o.id,
    groupVoteId: o.group_vote_id,
    activityId: o.activity_id ?? null,
    name: o.name,
    emoji: o.emoji ?? null,
    pricePerPerson: o.price_per_person != null ? Number(o.price_per_person) : null,
    notes: o.notes ?? null,
    sortOrder: o.sort_order ?? 0,
    votes: optionVotesByOption.get(o.id) ?? [],
  }));

  // Split options: FullBook (has activityId) vs manual
  const fullBookOptions = activityOptions.filter((o) => o.activityId !== null);
  const fullBookOptionIds = new Set(fullBookOptions.map((o) => o.id));
  const fullBookActivityIds = fullBookOptions.map((o) => o.activityId!);
  const maxParticipants = voteRow.max_participants;
  const today = new Date().toISOString().split('T')[0];

  // activityId → optionId for mapping venue_availability rows to the right option
  const activityIdToOptionId = new Map<string, string>();
  for (const o of fullBookOptions) {
    if (o.activityId) activityIdToOptionId.set(o.activityId, o.id);
  }

  // Fetch FullBook slots (venue_availability) and all group_vote_slots in parallel
  const [vaResult, groupSlotsResult] = await Promise.all([
    fullBookActivityIds.length > 0
      ? supabase
          .from('venue_availability')
          .select('id, activity_id, date, start_time, total_capacity, booked_count')
          .in('activity_id', fullBookActivityIds)
          .gte('date', today)
          .neq('status', 'blocked')
          .eq('manually_blocked', false)
          .order('date', { ascending: true })
          .order('start_time', { ascending: true })
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from('group_vote_slots')
      .select('id, group_vote_id, activity_option_id, venue_availability_id, slot_date, slot_time, votes_count, sort_order, notification_sent_at')
      .eq('group_vote_id', voteId)
      .order('slot_date', { ascending: true })
      .order('slot_time', { ascending: true }),
  ]);

  if (groupSlotsResult.error) return { success: false, reason: 'fetch_error' };

  // Filter FullBook slots by available capacity
  const vaRows = (vaResult.data ?? []).filter(
    (va) => (va.total_capacity - (va.booked_count ?? 0)) >= maxParticipants
  );

  // Manual slots: group_vote_slots NOT linked to a FullBook option
  const manualSlotRows = (groupSlotsResult.data ?? []).filter(
    (s) => !s.activity_option_id || !fullBookOptionIds.has(s.activity_option_id)
  );

  const fullBookSlotIds = vaRows.map((va) => va.id);
  const manualSlotIds = manualSlotRows.map((s) => s.id);

  // Fetch votes from both tables and FullBook notifications in parallel
  const [availabilityVotesResult, manualVotesResult, notificationsResult] = await Promise.all([
    fullBookSlotIds.length > 0
      ? supabase
          .from('group_vote_availability_votes')
          .select('id, venue_availability_id, voter_name, voter_session_id, created_at')
          .eq('group_vote_id', voteId)
          .in('venue_availability_id', fullBookSlotIds)
      : Promise.resolve({ data: [], error: null }),
    manualSlotIds.length > 0
      ? supabase
          .from('group_vote_votes')
          .select('id, group_vote_slot_id, voter_name, voter_session_id, created_at')
          .in('group_vote_slot_id', manualSlotIds)
      : Promise.resolve({ data: [], error: null }),
    fullBookSlotIds.length > 0
      ? supabase
          .from('group_vote_availability_notifications')
          .select('venue_availability_id, notified_at')
          .eq('group_vote_id', voteId)
          .in('venue_availability_id', fullBookSlotIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const availabilityVotesBySlot = new Map<string, GroupVoteSlotVote[]>();
  (availabilityVotesResult.data ?? []).forEach((v) => {
    const list = availabilityVotesBySlot.get(v.venue_availability_id) ?? [];
    list.push({ id: v.id, voterName: v.voter_name, voterSessionId: v.voter_session_id, createdAt: v.created_at });
    availabilityVotesBySlot.set(v.venue_availability_id, list);
  });

  const manualVotesBySlot = new Map<string, GroupVoteSlotVote[]>();
  (manualVotesResult.data ?? []).forEach((v) => {
    const list = manualVotesBySlot.get(v.group_vote_slot_id) ?? [];
    list.push({ id: v.id, voterName: v.voter_name, voterSessionId: v.voter_session_id, createdAt: v.created_at });
    manualVotesBySlot.set(v.group_vote_slot_id, list);
  });

  const notificationBySlot = new Map<string, string>();
  (notificationsResult.data ?? []).forEach((n) => {
    notificationBySlot.set(n.venue_availability_id, n.notified_at);
  });

  const fullBookSlots: GroupVoteSlot[] = vaRows.map((va, i) => {
    const votes = availabilityVotesBySlot.get(va.id) ?? [];
    return {
      id: va.id,
      groupVoteId: voteId,
      activityOptionId: activityIdToOptionId.get(va.activity_id) ?? null,
      venueAvailabilityId: va.id,
      slotDate: va.date,
      slotTime: (va.start_time as string).slice(0, 5),
      votesCount: votes.length,
      sortOrder: i,
      notificationSentAt: notificationBySlot.get(va.id) ?? null,
      votes,
      isFullBook: true,
    };
  });

  const manualSlots: GroupVoteSlot[] = manualSlotRows.map((s) => ({
    id: s.id,
    groupVoteId: s.group_vote_id,
    activityOptionId: s.activity_option_id ?? null,
    venueAvailabilityId: s.venue_availability_id ?? null,
    slotDate: s.slot_date,
    slotTime: s.slot_time,
    votesCount: s.votes_count ?? 0,
    sortOrder: s.sort_order ?? 0,
    notificationSentAt: s.notification_sent_at ?? null,
    votes: manualVotesBySlot.get(s.id) ?? [],
    isFullBook: false,
  }));

  const slots = [...fullBookSlots, ...manualSlots];

  const comments: GroupVoteComment[] = commentRows.map((c) => ({
    id: c.id,
    groupVoteId: c.group_vote_id,
    voterName: c.voter_name,
    voterSessionId: c.voter_session_id,
    body: c.body,
    createdAt: c.created_at,
  }));

  let venueName: string | null = null;
  const primaryActivityId = voteRow.activity_id ?? activityOptions.find((o) => o.activityId)?.activityId;
  if (primaryActivityId) {
    const { data: actRow } = await supabase
      .from('activities')
      .select('professional_id')
      .eq('id', primaryActivityId)
      .single();
    if (actRow?.professional_id) {
      const { data: proRow } = await supabase
        .from('professionals')
        .select('display_name, business_name')
        .eq('id', actRow.professional_id)
        .single();
      if (proRow) venueName = proRow.display_name || proRow.business_name || null;
    }
  }

  return {
    success: true,
    vote: {
      id: voteRow.id,
      title: voteRow.title ?? deriveTitle(voteRow.activity_name),
      activityName: voteRow.activity_name,
      activityId: voteRow.activity_id ?? activityOptions.find((o) => o.activityId)?.activityId ?? null,
      venueName,
      maxParticipants: voteRow.max_participants,
      numberOfGames: voteRow.number_of_games ?? 1,
      organizerId: voteRow.organizer_id,
      status: voteRow.status as 'active' | 'completed' | 'expired',
      expiresAt: voteRow.expires_at,
      createdAt: voteRow.created_at,
      activityOptions,
      slots,
      comments,
    },
  };
}

// ─── Create (full params) ─────────────────────────────────────────────────────

export async function createGroupVote(
  params: CreateGroupVoteParams
): Promise<{ success: boolean; groupVote?: GroupVote; error?: string }> {
  try {
    const { title, activityOptions, dateSlots, maxParticipants, numberOfGames = 1, organizerId } = params;

    if (activityOptions.length === 0) return { success: false, error: 'Ajoute au moins une activité.' };
    if (dateSlots.length === 0) return { success: false, error: 'Propose au moins une date.' };

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const firstFullBook = activityOptions.find((o) => o.activityId);
    const activityName = firstFullBook?.name ?? activityOptions[0].name;
    const activityId = firstFullBook?.activityId ?? null;

    const { data: voteRow, error: voteError } = await supabase
      .from('group_votes')
      .insert({
        title,
        activity_id: activityId,
        activity_name: activityName,
        max_participants: maxParticipants,
        number_of_games: numberOfGames,
        organizer_id: organizerId,
        status: 'active',
        expires_at: expiresAt.toISOString(),
      })
      .select('id')
      .single();

    if (voteError || !voteRow) {
      return { success: false, error: voteError?.message ?? 'Erreur lors de la création du vote' };
    }

    const voteId = voteRow.id;

    // Insert activity options first to get their IDs for slot linking
    const { data: insertedOptions, error: optionsErr } = await supabase
      .from('group_vote_activity_options')
      .insert(
        activityOptions.map((o, i) => ({
          group_vote_id: voteId,
          activity_id: o.activityId ?? null,
          name: o.name,
          emoji: o.emoji ?? null,
          price_per_person: o.pricePerPerson ?? null,
          notes: o.notes ?? null,
          sort_order: i,
        }))
      )
      .select('id');

    if (optionsErr || !insertedOptions) {
      await supabase.from('group_votes').delete().eq('id', voteId);
      return { success: false, error: 'Erreur lors de la création des options' };
    }

    if (dateSlots.length > 0) {
      const { error: slotsErr } = await supabase
        .from('group_vote_slots')
        .insert(
          dateSlots.map((s, i) => ({
            group_vote_id: voteId,
            activity_option_id: s.activityOptionId ?? null,
            venue_availability_id: s.venueAvailabilityId ?? null,
            slot_date: s.date,
            slot_time: s.time,
            sort_order: i,
          }))
        );

      if (slotsErr) {
        await supabase.from('group_votes').delete().eq('id', voteId);
        return { success: false, error: 'Erreur lors de la création des créneaux' };
      }
    }

    const result = await getGroupVote(voteId);
    if (!result.success) return { success: false, error: 'Vote créé mais impossible à charger' };
    return { success: true, groupVote: result.vote };
  } catch (err) {
    console.error('createGroupVote error:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
  }
}

// ─── Create (open — from hero/dashboard, no activities or slots required upfront) ──

export interface CreateOpenGroupVoteParams {
  title: string;
  maxParticipants: number;
  organizerId: string;
}

export async function createOpenGroupVote(
  params: CreateOpenGroupVoteParams
): Promise<{ success: boolean; groupVote?: GroupVote; error?: string }> {
  try {
    const { title, maxParticipants, organizerId } = params;

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { data: voteRow, error: voteError } = await supabase
      .from('group_votes')
      .insert({
        title,
        activity_id: null,
        activity_name: title,
        max_participants: maxParticipants,
        number_of_games: 1,
        organizer_id: organizerId,
        status: 'active',
        expires_at: expiresAt.toISOString(),
      })
      .select('id')
      .single();

    if (voteError || !voteRow) {
      return { success: false, error: voteError?.message ?? 'Erreur lors de la création du vote' };
    }

    const result = await getGroupVote(voteRow.id);
    if (!result.success) return { success: false, error: 'Vote créé mais impossible à charger' };
    return { success: true, groupVote: result.vote };
  } catch (err) {
    console.error('createOpenGroupVote error:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
  }
}

// ─── Create (quick — from booking form, single activity; slots served live from venue_availability) ──

export async function createGroupVoteQuick(
  params: CreateGroupVoteQuickParams
): Promise<{ success: boolean; groupVote?: GroupVote; error?: string }> {
  try {
    const { activityId, activityName, title, maxParticipants, numberOfGames = 1, organizerId } = params;

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);
    const voteTitle = title?.trim() || deriveTitle(activityName);

    const { data: voteRow, error: voteError } = await supabase
      .from('group_votes')
      .insert({
        title: voteTitle,
        activity_id: activityId,
        activity_name: activityName,
        max_participants: maxParticipants,
        number_of_games: numberOfGames,
        organizer_id: organizerId,
        status: 'active',
        expires_at: expiresAt.toISOString(),
      })
      .select('id')
      .single();

    if (voteError || !voteRow) {
      return { success: false, error: voteError?.message ?? 'Erreur lors de la création du vote' };
    }

    const voteId = voteRow.id;

    const { error: optionErr } = await supabase
      .from('group_vote_activity_options')
      .insert({
        group_vote_id: voteId,
        activity_id: activityId,
        name: activityName,
        sort_order: 0,
      });

    if (optionErr) {
      await supabase.from('group_votes').delete().eq('id', voteId);
      return { success: false, error: 'Erreur lors de la création de l\'option' };
    }

    const result = await getGroupVote(voteId);
    if (!result.success) return { success: false, error: 'Vote créé mais impossible à charger' };
    return { success: true, groupVote: result.vote };
  } catch (err) {
    console.error('createGroupVoteQuick error:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
  }
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export async function deleteGroupVote(
  voteId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return { success: false, error: 'Connecte-toi pour supprimer ce vote.' };

    const res = await fetch(`${SUPABASE_URL}/functions/v1/delete-group-vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ voteId }),
    });

    const json = await res.json();
    if (!res.ok || json.success === false) {
      return { success: false, error: json.error || 'Impossible de supprimer le vote' };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Erreur réseau' };
  }
}

// ─── Vote on a date slot ──────────────────────────────────────────────────────

export async function addSlotVote(
  voteId: string,
  slot: GroupVoteSlot,
  voterName: string,
  voterSessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const body = slot.isFullBook
      ? { voteId, venueAvailabilityId: slot.id, activityOptionId: slot.activityOptionId, voterName, voterSessionId }
      : { voteId, groupVoteSlotId: slot.id, voterName, voterSessionId };
    const res = await fetch(`${SUPABASE_URL}/functions/v1/add-group-vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.success === false) return { success: false, error: json.error };
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Erreur réseau' };
  }
}

export async function removeSlotVote(
  voteId: string,
  slot: GroupVoteSlot,
  voterSessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const body = slot.isFullBook
      ? { voteId, venueAvailabilityId: slot.id, voterSessionId }
      : { voteId, groupVoteSlotId: slot.id, voterSessionId };
    const res = await fetch(`${SUPABASE_URL}/functions/v1/remove-group-vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.success === false) return { success: false, error: json.error };
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Erreur réseau' };
  }
}

// ─── Vote on an activity option (kept for backward compat, not used for open votes) ──

export async function addOptionVote(
  voteId: string,
  optionId: string,
  voterName: string,
  voterSessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/add-option-vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voteId, optionId, voterName, voterSessionId }),
    });
    const json = await res.json();
    if (!res.ok || json.success === false) return { success: false, error: json.error };
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Erreur réseau' };
  }
}

export async function removeOptionVote(
  voteId: string,
  optionId: string,
  voterSessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/remove-option-vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voteId, optionId, voterSessionId }),
    });
    const json = await res.json();
    if (!res.ok || json.success === false) return { success: false, error: json.error };
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Erreur réseau' };
  }
}

// ─── Comments ─────────────────────────────────────────────────────────────────

export async function addComment(
  voteId: string,
  voterName: string,
  voterSessionId: string,
  body: string
): Promise<{ success: boolean; comment?: GroupVoteComment; error?: string }> {
  const { data, error } = await supabase
    .from('group_vote_comments')
    .insert({ group_vote_id: voteId, voter_name: voterName, voter_session_id: voterSessionId, body })
    .select()
    .single();

  if (error || !data) return { success: false, error: error?.message ?? "Erreur lors de l'envoi" };

  return {
    success: true,
    comment: {
      id: data.id,
      groupVoteId: data.group_vote_id,
      voterName: data.voter_name,
      voterSessionId: data.voter_session_id,
      body: data.body,
      createdAt: data.created_at,
    },
  };
}

// ─── Activity search ──────────────────────────────────────────────────────────

export interface ActivitySearchResult {
  id: string;
  name: string;
  city: string;
  shortDescription: string | null;
  venueName: string | null;
  matchedVia: 'activity' | 'venue';
  capacity: number | null;
}

type ActivityRow = {
  id: string;
  name: string;
  city: string;
  short_description: string | null;
  professional_id: string | null;
  capacity: number | null;
};

type ProfessionalRow = {
  id: string;
  business_name: string;
  display_name: string | null;
};

const MAX_RESULTS = 10;
const NAME_QUERY_LIMIT = 8;
const VENUE_QUERY_LIMIT = 6;
const VENUE_ACTIVITY_LIMIT = 12;

function pickVenueName(pro: ProfessionalRow | undefined): string | null {
  if (!pro) return null;
  return pro.display_name || pro.business_name || null;
}

/**
 * Search FullBook activities by activity name OR venue name.
 * Avoids PostgREST join on professionals (FK points to profiles, not professionals).
 */
export async function searchActivities(query: string): Promise<ActivitySearchResult[]> {
  if (!query.trim()) return [];
  const q = query.trim();
  const ACTIVITY_SELECT = 'id, name, city, short_description, professional_id, capacity';

  const [nameRes, venueRes] = await Promise.all([
    supabase
      .from('activities')
      .select(ACTIVITY_SELECT)
      .eq('is_active', true)
      .ilike('name', `%${q}%`)
      .order('name')
      .limit(NAME_QUERY_LIMIT),
    supabase
      .from('professionals')
      .select('id, business_name, display_name')
      .or(`business_name.ilike.%${q}%,display_name.ilike.%${q}%`)
      .limit(VENUE_QUERY_LIMIT),
  ]);

  if (nameRes.error) console.error('[searchActivities] activity name query failed:', nameRes.error);
  if (venueRes.error) console.error('[searchActivities] venue name query failed:', venueRes.error);

  const byName = (nameRes.data as ActivityRow[] | null) ?? [];
  const matchedPros = (venueRes.data as ProfessionalRow[] | null) ?? [];

  let byVenue: ActivityRow[] = [];
  if (matchedPros.length > 0) {
    const { data: venueActivities, error: venueActErr } = await supabase
      .from('activities')
      .select(ACTIVITY_SELECT)
      .eq('is_active', true)
      .in('professional_id', matchedPros.map((p) => p.id))
      .order('name')
      .limit(VENUE_ACTIVITY_LIMIT);

    if (venueActErr) console.error('[searchActivities] venue activities query failed:', venueActErr);
    byVenue = (venueActivities as ActivityRow[] | null) ?? [];
  }

  const prosById = new Map<string, ProfessionalRow>();
  for (const pro of matchedPros) prosById.set(pro.id, pro);

  const missingProIds = Array.from(
    new Set(
      byName
        .map((a) => a.professional_id)
        .filter((id): id is string => !!id && !prosById.has(id))
    )
  );

  if (missingProIds.length > 0) {
    const { data: extraPros, error: extraProErr } = await supabase
      .from('professionals')
      .select('id, business_name, display_name')
      .in('id', missingProIds);

    if (extraProErr) console.error('[searchActivities] professionals enrichment failed:', extraProErr);
    for (const pro of (extraPros as ProfessionalRow[] | null) ?? []) {
      prosById.set(pro.id, pro);
    }
  }

  const seen = new Set<string>();
  const results: ActivitySearchResult[] = [];

  const push = (row: ActivityRow, matchedVia: 'activity' | 'venue') => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    const pro = row.professional_id ? prosById.get(row.professional_id) : undefined;
    results.push({
      id: row.id,
      name: row.name,
      city: row.city,
      shortDescription: row.short_description,
      venueName: pickVenueName(pro),
      matchedVia,
      capacity: row.capacity ?? null,
    });
  };

  for (const row of byVenue) {
    if (results.length >= MAX_RESULTS) break;
    push(row, 'venue');
  }
  for (const row of byName) {
    if (results.length >= MAX_RESULTS) break;
    push(row, 'activity');
  }

  return results;
}

// ─── Remove activity option from existing vote ───────────────────────────────

export async function removeActivityOptionFromVote(
  voteId: string,
  optionId: string
): Promise<{ success: boolean; error?: string }> {
  // Delete slots linked to this option first (FK is ON DELETE SET NULL, so we clean up manually)
  const { error: slotsErr } = await supabase
    .from('group_vote_slots')
    .delete()
    .eq('group_vote_id', voteId)
    .eq('activity_option_id', optionId);

  if (slotsErr) return { success: false, error: slotsErr.message };

  const { error } = await supabase
    .from('group_vote_activity_options')
    .delete()
    .eq('id', optionId)
    .eq('group_vote_id', voteId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ─── Add activity option to existing vote ─────────────────────────────────────

export async function addActivityOptionToVote(
  voteId: string,
  option: { name: string; emoji?: string; activityId?: string }
): Promise<{ success: boolean; error?: string }> {
  const { data: existing } = await supabase
    .from('group_vote_activity_options')
    .select('sort_order')
    .eq('group_vote_id', voteId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .single();

  const nextOrder = existing ? (existing.sort_order ?? 0) + 1 : 0;

  const { error } = await supabase.from('group_vote_activity_options').insert({
    group_vote_id: voteId,
    activity_id: option.activityId ?? null,
    name: option.name.trim(),
    emoji: option.emoji ?? null,
    sort_order: nextOrder,
  });

  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ─── Add / remove slots (organizer) ──────────────────────────────────────────

export async function addSlotToVote(
  voteId: string,
  date: string,
  time: string,
  activityOptionId?: string
): Promise<{ success: boolean; error?: string }> {
  const { data: existing } = await supabase
    .from('group_vote_slots')
    .select('sort_order')
    .eq('group_vote_id', voteId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .single();

  const nextOrder = existing ? (existing.sort_order ?? 0) + 1 : 0;

  const { error } = await supabase.from('group_vote_slots').insert({
    group_vote_id: voteId,
    activity_option_id: activityOptionId ?? null,
    slot_date: date,
    slot_time: time,
    sort_order: nextOrder,
  });

  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function removeSlotFromVote(
  voteId: string,
  slotId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('group_vote_slots')
    .delete()
    .eq('id', slotId)
    .eq('group_vote_id', voteId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ─── Organizer helpers ────────────────────────────────────────────────────────

export async function getAllGroupVotesForOrganizer(organizerId: string): Promise<GroupVote[]> {
  const { data: rows, error } = await supabase
    .from('group_votes')
    .select('id')
    .eq('organizer_id', organizerId)
    .order('created_at', { ascending: false });

  if (error || !rows?.length) return [];

  const results = await Promise.all(rows.map((r) => getGroupVote(r.id)));
  return results
    .filter((r): r is { success: true; vote: GroupVote } => r.success)
    .map((r) => r.vote)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export async function getActiveGroupVotesForOrganizer(organizerId: string): Promise<GroupVote[]> {
  const { data: rows, error } = await supabase
    .from('group_votes')
    .select('id')
    .eq('organizer_id', organizerId)
    .eq('status', 'active')
    .gt('expires_at', new Date().toISOString());

  if (error || !rows?.length) return [];

  const results = await Promise.all(rows.map((r) => getGroupVote(r.id)));
  return results
    .filter((r): r is { success: true; vote: GroupVote } => r.success)
    .map((r) => r.vote)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

// ─── Booking prefill ──────────────────────────────────────────────────────────

export function getBookingPrefillFromGroupVote(
  groupVote: GroupVote,
  slotId?: string
): { date: string; time: string; participants: number; numberOfGames: number } | null {
  const votesNeeded = Math.max(1, groupVote.maxParticipants - 1);
  const readySlots = groupVote.slots.filter((s) => s.votesCount >= votesNeeded);
  if (readySlots.length === 0) return null;
  const slot = slotId ? readySlots.find((s) => s.id === slotId) : readySlots[0];
  if (!slot) return null;
  return {
    date: slot.slotDate,
    time: formatSlotTime(slot.slotTime),
    participants: groupVote.maxParticipants,
    numberOfGames: groupVote.numberOfGames,
  };
}

// ─── Share URL ────────────────────────────────────────────────────────────────

export function generateShareableUrl(voteId: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/group-vote/${voteId}`;
}

// ─── Voter session ────────────────────────────────────────────────────────────

export function getOrCreateVoterSessionId(voteId: string): string {
  const key = `fullbook_voter_session_${voteId}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = `voter_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

// ─── Unique voter count ───────────────────────────────────────────────────────

export function countUniqueVoters(groupVote: GroupVote): number {
  const sessions = new Set<string>();
  // Only count slot voters — slot votes are the single voting mechanism for open votes
  groupVote.slots.forEach((s) => s.votes.forEach((v) => sessions.add(v.voterSessionId)));
  return sessions.size;
}

// ─── Activity vote tallies (unique voters per activity, derived from slots) ───

export function getUniqueVotersPerOption(groupVote: GroupVote): Map<string, number> {
  const tallies = new Map<string, number>();
  for (const option of groupVote.activityOptions) {
    const sessions = new Set<string>();
    groupVote.slots
      .filter((s) => s.activityOptionId === option.id)
      .forEach((s) => s.votes.forEach((v) => sessions.add(v.voterSessionId)));
    tallies.set(option.id, sessions.size);
  }
  return tallies;
}

// ─── Winner detection ─────────────────────────────────────────────────────────

export function getWinnerSlot(groupVote: GroupVote): GroupVoteSlot | null {
  if (groupVote.slots.length === 0) return null;
  const best = groupVote.slots.reduce((a, b) => (b.votesCount > a.votesCount ? b : a));
  return best.votesCount > 0 ? best : null;
}

export function getWinnerOption(groupVote: GroupVote): GroupVoteActivityOption | null {
  if (groupVote.activityOptions.length === 0) return null;

  const winnerSlot = getWinnerSlot(groupVote);

  // Open vote: winner activity is derived from the winning slot's activity_option_id
  if (winnerSlot?.activityOptionId) {
    return groupVote.activityOptions.find((o) => o.id === winnerSlot.activityOptionId) ?? null;
  }

  // Fallback for quick votes (single activity, slots with null activity_option_id)
  if (groupVote.activityOptions.length === 1) return groupVote.activityOptions[0];

  return null;
}
